import { useCallback, useEffect, useState } from 'react'
import { Loader2, Search, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import {
  type OriginalCandidate,
  type OriginalLinkResult,
  useOriginals,
} from '../../hooks/useOriginals'

export interface OriginalDocPickerProps {
  invoiceId: string
  // Current persisted values (populated by extraction or prior user actions)
  currentNumber?: string | null
  currentDate?: string | null // YYYY-MM-DD
  currentSaldoInvoiceId?: string | null
  currentMirageDocHeaderId?: number | null
  // Disable interaction when invoice is exported / locked
  readOnly?: boolean
  onLinkChanged?: (result: OriginalLinkResult) => void
}

export function OriginalDocPicker({
  invoiceId,
  currentNumber,
  currentDate,
  currentSaldoInvoiceId,
  currentMirageDocHeaderId,
  readOnly,
  onLinkChanged,
}: OriginalDocPickerProps) {
  const { searchCandidates, linkOriginal, clearLink } = useOriginals(invoiceId)

  const [draftNumber, setDraftNumber] = useState(currentNumber ?? '')
  const [draftDate, setDraftDate] = useState(currentDate ?? '')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [candidates, setCandidates] = useState<OriginalCandidate[]>([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setDraftNumber(currentNumber ?? '')
    setDraftDate(currentDate ?? '')
  }, [currentNumber, currentDate])

  const isLinked = Boolean(currentSaldoInvoiceId || currentMirageDocHeaderId)

  const runSearch = useCallback(
    async (q: string) => {
      setSearching(true)
      setSearchError(null)
      try {
        const rows = await searchCandidates(q)
        setCandidates(rows ?? [])
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Search failed'
        setSearchError(message)
        setCandidates([])
      } finally {
        setSearching(false)
      }
    },
    [searchCandidates]
  )

  const openDialog = useCallback(() => {
    setDialogOpen(true)
    setSearchQuery(draftNumber)
    void runSearch(draftNumber)
  }, [draftNumber, runSearch])

  const handleSelectCandidate = useCallback(
    async (c: OriginalCandidate) => {
      setSaving(true)
      try {
        const result = await linkOriginal({
          saldo_invoice_id: c.saldo_invoice_id ?? null,
          mirage_doc_header_id: c.mirage_doc_header_id ?? null,
          doc_number: c.doc_number,
          doc_date: c.doc_date,
        })
        onLinkChanged?.(result)
        setDraftNumber(c.doc_number)
        setDraftDate(c.doc_date)
        setDialogOpen(false)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Link failed'
        setSearchError(message)
      } finally {
        setSaving(false)
      }
    },
    [linkOriginal, onLinkChanged]
  )

  const handleSaveText = useCallback(async () => {
    setSaving(true)
    try {
      const result = await linkOriginal({
        saldo_invoice_id: null,
        mirage_doc_header_id: null,
        doc_number: draftNumber,
        doc_date: draftDate || undefined,
      })
      onLinkChanged?.(result)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Save failed'
      setSearchError(message)
    } finally {
      setSaving(false)
    }
  }, [draftDate, draftNumber, linkOriginal, onLinkChanged])

  const handleClear = useCallback(async () => {
    setSaving(true)
    try {
      const result = await clearLink()
      onLinkChanged?.(result)
      setDraftNumber('')
      setDraftDate('')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Clear failed'
      setSearchError(message)
    } finally {
      setSaving(false)
    }
  }, [clearLink, onLinkChanged])

  return (
    <Card className="border border-neutral-200 shadow-sm">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <h3 className="font-text-sm-medium text-black">Original Document</h3>
          <span className="text-xs text-gray-500">
            (referenced invoice for this credit/debit note)
          </span>
          {isLinked && (
            <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              {currentSaldoInvoiceId && currentMirageDocHeaderId
                ? 'Linked in app + Mirage'
                : currentSaldoInvoiceId
                  ? 'Linked in app'
                  : 'Linked in Mirage'}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-xs text-neutral-500">Original number</Label>
            <Input
              value={draftNumber}
              onChange={(e) => setDraftNumber(e.target.value)}
              placeholder="e.g. 3000003923"
              disabled={readOnly || saving}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-neutral-500">Original date</Label>
            <Input
              type="date"
              value={draftDate}
              onChange={(e) => setDraftDate(e.target.value)}
              disabled={readOnly || saving}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openDialog}
            disabled={readOnly}
          >
            <Search className="mr-1 h-3.5 w-3.5" />
            Search app + Mirage
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSaveText}
            disabled={
              readOnly ||
              saving ||
              (draftNumber === (currentNumber ?? '') &&
                draftDate === (currentDate ?? ''))
            }
          >
            {saving ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : null}
            Save as text
          </Button>
          {(isLinked || draftNumber || draftDate) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={readOnly || saving}
            >
              <X className="mr-1 h-3.5 w-3.5" />
              Clear link
            </Button>
          )}
        </div>

        {searchError && !dialogOpen && (
          <div className="text-xs text-red-600">{searchError}</div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Search original document</DialogTitle>
              <DialogDescription>
                Looking up invoices in the app and Mirage for the same
                counterparty. Pick the matching original to link this credit
                note.
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-center gap-2">
              <Input
                placeholder="Filter by document number prefix"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    void runSearch(searchQuery)
                  }
                }}
              />
              <Button
                type="button"
                onClick={() => void runSearch(searchQuery)}
                disabled={searching}
              >
                {searching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>

            {searchError && (
              <div className="text-xs text-red-600">{searchError}</div>
            )}

            <div className="max-h-[420px] overflow-auto rounded border border-neutral-200">
              {searching ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
                </div>
              ) : candidates.length === 0 ? (
                <div className="px-4 py-12 text-center text-sm text-neutral-500">
                  No candidates found.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Source</th>
                      <th className="px-3 py-2 text-left font-medium">Number</th>
                      <th className="px-3 py-2 text-left font-medium">Date</th>
                      <th className="px-3 py-2 text-left font-medium">Partner</th>
                      <th className="px-3 py-2 text-right font-medium">Amount</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c, idx) => (
                      <tr
                        key={`${c.source}-${c.saldo_invoice_id ?? c.mirage_doc_header_id ?? idx}`}
                        className="border-t border-neutral-100 hover:bg-neutral-50"
                      >
                        <td className="px-3 py-2">
                          <SourceBadge source={c.source} />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {c.doc_number}
                        </td>
                        <td className="px-3 py-2 text-xs">{c.doc_date}</td>
                        <td className="px-3 py-2 text-xs text-neutral-700">
                          {c.partner_name ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-right text-xs">
                          {c.doc_amount ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => void handleSelectCandidate(c)}
                            disabled={saving}
                          >
                            Select
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}

function SourceBadge({ source }: { source: OriginalCandidate['source'] }) {
  const styles: Record<OriginalCandidate['source'], string> = {
    saldo: 'bg-blue-50 text-blue-700',
    mirage: 'bg-amber-50 text-amber-700',
    both: 'bg-emerald-50 text-emerald-700',
  }
  const label: Record<OriginalCandidate['source'], string> = {
    saldo: 'Demo',
    mirage: 'Mirage',
    both: 'Both',
  }
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${styles[source]}`}
    >
      {label[source]}
    </span>
  )
}
